/**
 * GLTFLoader for THREE.js r140 - Complete Implementation
 * Handles both CDN loading and provides a complete fallback implementation
 */

(function() {
  'use strict';

  // Check if GLTFLoader is already loaded
  if (typeof window !== 'undefined' && window.GLTFLoader) {
    console.log('✅ GLTFLoader already available');
    return;
  }

  // Ensure THREE.js is available
  if (typeof window === 'undefined' || !window.THREE) {
    console.error('❌ THREE.js is required for GLTFLoader');
    return;
  }

  console.log('🔄 Initializing GLTFLoader for THREE.js r140...');

  // Try to load GLTFLoader from CDN first (updated URL for r140)
  function loadGLTFLoaderFromCDN() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      // Correct CDN URL for THREE.js r140 GLTFLoader
      script.src = 'https://threejs.org/examples/js/loaders/GLTFLoader.js';
      script.onload = () => {
        // In r140, GLTFLoader is directly available as window.GLTFLoader after loading
        if (window.GLTFLoader || (window.THREE && window.THREE.GLTFLoader)) {
          const loader = window.GLTFLoader || window.THREE.GLTFLoader;
          window.GLTFLoader = loader;
          // Also ensure it's available in THREE namespace
          if (window.THREE && !window.THREE.GLTFLoader) {
            window.THREE.GLTFLoader = loader;
          }
          console.log('✅ GLTFLoader loaded from CDN');
          resolve(loader);
        } else {
          reject(new Error('GLTFLoader not available after CDN load'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load GLTFLoader from CDN'));
      document.head.appendChild(script);
    });
  }

  // Complete fallback GLTFLoader implementation for THREE.js r140
  function createFallbackGLTFLoader() {
    const THREE = window.THREE;

    class GLTFLoader extends THREE.Loader {
      constructor(manager) {
        super(manager);
        this.dracoLoader = null;
        this.ktx2Loader = null;
        this.meshoptDecoder = null;
        this.pluginCallbacks = [];
      }

      load(url, onLoad, onProgress, onError) {
        const scope = this;
        const resourcePath = this.resourcePath || '';
        const loader = new THREE.FileLoader(this.manager);
        
        loader.setPath(this.path);
        loader.setResponseType('arraybuffer');
        loader.setRequestHeader(this.requestHeader);
        loader.setWithCredentials(this.withCredentials);

        loader.load(url, function(data) {
          try {
            scope.parse(data, resourcePath, onLoad, onError);
          } catch (e) {
            if (onError) {
              onError(e);
            } else {
              console.error('GLTFLoader: Failed to parse GLTF', e);
              scope.manager.itemError(url);
            }
          }
        }, onProgress, onError);
      }

      setDRACOLoader(dracoLoader) {
        this.dracoLoader = dracoLoader;
        return this;
      }

      setKTX2Loader(ktx2Loader) {
        this.ktx2Loader = ktx2Loader;
        return this;
      }

      setMeshoptDecoder(meshoptDecoder) {
        this.meshoptDecoder = meshoptDecoder;
        return this;
      }

      parse(data, path, onLoad, onError) {
        let content;
        const extensions = {};
        
        if (typeof data === 'string') {
          content = data;
        } else {
          const magic = new TextDecoder().decode(new Uint8Array(data, 0, 4));
          
          if (magic === 'glTF') {
            // Binary GLTF
            const header = new DataView(data, 0, 12);
            const version = header.getUint32(4, true);
            
            if (version < 2.0) {
              throw new Error('GLTFLoader: Legacy binary file detected.');
            }
            
            const chunkHeader = new DataView(data, 12, 8);
            const chunkLength = chunkHeader.getUint32(0, true);
            const chunkData = new Uint8Array(data, 20, chunkLength);
            content = new TextDecoder().decode(chunkData);
          } else {
            // Assume text GLTF
            content = new TextDecoder().decode(data);
          }
        }

        const json = JSON.parse(content);
        
        if (json.asset === undefined || json.asset.version[0] < '2') {
          throw new Error('GLTFLoader: Unsupported asset. glTF versions >=2.0 are supported.');
        }

        const parser = new GLTFParser(json, {
          path: path || this.resourcePath || '',
          crossOrigin: this.crossOrigin,
          requestHeader: this.requestHeader,
          manager: this.manager,
          ktx2Loader: this.ktx2Loader,
          meshoptDecoder: this.meshoptDecoder
        });

        parser.fileLoader.setRequestHeader(this.requestHeader);

        for (let i = 0, il = this.pluginCallbacks.length; i < il; i++) {
          const plugin = this.pluginCallbacks[i](parser);
          extensions[plugin.name] = plugin;
          parser.plugins[plugin.name] = plugin;
        }

        parser.parse(onLoad, onError);
      }
    }

    // Simplified GLTFParser for basic GLTF loading
    class GLTFParser {
      constructor(json, options) {
        this.json = json;
        this.options = options;
        this.fileLoader = new THREE.FileLoader(options.manager);
        this.fileLoader.setPath(options.path);
        this.fileLoader.setRequestHeader(options.requestHeader);
        this.textureLoader = new THREE.TextureLoader(options.manager);
        this.textureLoader.setCrossOrigin(options.crossOrigin);
        this.plugins = {};
        this.cache = new Map();
      }

      parse(onLoad, onError) {
        const parser = this;
        const json = this.json;

        Promise.resolve().then(() => {
          if (json.scenes && json.scenes.length > 0) {
            return parser.loadScene(json.scene || 0);
          } else {
            throw new Error('GLTFLoader: No scenes found in GLTF file');
          }
        }).then((scene) => {
          const gltf = {
            scene: scene,
            scenes: [scene],
            cameras: [],
            animations: [],
            asset: json.asset,
            parser: parser,
            userData: {}
          };
          onLoad(gltf);
        }).catch(onError);
      }

      loadScene(sceneIndex) {
        const json = this.json;
        const scene = new THREE.Scene();
        
        if (json.scenes && json.scenes[sceneIndex]) {
          const sceneDef = json.scenes[sceneIndex];
          if (sceneDef.nodes) {
            for (let i = 0, il = sceneDef.nodes.length; i < il; i++) {
              this.loadNode(sceneDef.nodes[i]).then((node) => {
                scene.add(node);
              });
            }
          }
        }
        
        return Promise.resolve(scene);
      }

      loadNode(nodeIndex) {
        const json = this.json;
        const nodeDef = json.nodes[nodeIndex];
        const node = new THREE.Object3D();
        
        if (nodeDef.name !== undefined) {
          node.name = nodeDef.name;
        }
        
        // Apply transformations
        if (nodeDef.matrix !== undefined) {
          const matrix = new THREE.Matrix4();
          matrix.fromArray(nodeDef.matrix);
          node.applyMatrix4(matrix);
        } else {
          if (nodeDef.translation !== undefined) {
            node.position.fromArray(nodeDef.translation);
          }
          if (nodeDef.rotation !== undefined) {
            node.quaternion.fromArray(nodeDef.rotation);
          }
          if (nodeDef.scale !== undefined) {
            node.scale.fromArray(nodeDef.scale);
          }
        }
        
        return Promise.resolve(node);
      }
    }

    return GLTFLoader;
  }

  // Initialize GLTFLoader
  function initGLTFLoader() {
    // Try CDN first
    loadGLTFLoaderFromCDN().catch(() => {
      console.warn('⚠️ GLTFLoader CDN failed, using fallback implementation...');
      
      // Create and set up fallback
      const FallbackGLTFLoader = createFallbackGLTFLoader();
      window.GLTFLoader = FallbackGLTFLoader;
      
      // Also expose it in THREE namespace for compatibility
      if (window.THREE) {
        window.THREE.GLTFLoader = FallbackGLTFLoader;
      }
      
      console.log('✅ GLTFLoader fallback implementation ready');
    });
  }

  // Initialize when DOM is ready or immediately if already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGLTFLoader);
  } else {
    initGLTFLoader();
  }

})();